import { Briefcase } from 'lucide-react';
import type { JobAdvertisement } from '@/types/userAdmin';

interface JobAdvertisementListProps {
  jobs: JobAdvertisement[];
  limit?: number;
}

/**
 * JobAdvertisementList component displays job advertisements posted by a user
 * 
 * @param {JobAdvertisementListProps} props - Component properties
 * @returns {JSX.Element} Rendered job advertisement list
 */
export function JobAdvertisementList({ jobs, limit }: JobAdvertisementListProps) {
  const displayedJobs = limit ? jobs.slice(0, limit) : jobs;
  const hasMore = limit && jobs.length > limit;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      case 'archived':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  return (
    <div className="space-y-2">
      {displayedJobs.map((job) => (
        <div
          key={job.id}
          className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200"
        >
          <Briefcase className="text-primary-600 mt-0.5 flex-shrink-0" size={18} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-neutral-900 truncate">{job.title}</p>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(job.status)}`}>
                {job.status}
              </span>
            </div>
            <p className="text-xs text-neutral-600">
              Posted {formatDate(job.created_at)}
            </p>
          </div>
        </div>
      ))}

      {hasMore && (
        <p className="text-sm text-center text-neutral-600 mt-2">
          and {jobs.length - limit!} more...
        </p>
      )}
    </div>
  );
}
